import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { KnowledgeBaseFindInterface } from '@zaoblako/shared/modules/ai/dtos/knowledge-base/knowledge-base-find.dto';
import {
  KnowledgeBaseFormDto,
  KnowledgeBaseFormInterface
} from '@zaoblako/shared/modules/ai/dtos/knowledge-base/knowledge-base-form.dto';
import { KnowledgeBaseCategoryEnum } from '@zaoblako/shared/modules/ai/enums/knowledge-base-category.enum';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { AiKnowledgeBaseService } from '@zaoblako/web/core/services/directory/ai-knowledge-base.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FileUploaderComponent } from '@zaoblako/web/shared/components/file-uploader/file-uploader.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { KnowledgeBaseCategoryNamePipe } from '@zaoblako/web/shared/pipes/knowledge-base-category-name.pipe';
import { CardModule } from 'primeng/card';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-ai-knowledge-base-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    ButtonComponent,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SingleSelectComponent,
    KnowledgeBaseCategoryNamePipe,
    FileUploaderComponent,
    EditorComponent
  ],
  templateUrl: './ai-knowledge-base-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiKnowledgeBaseFormComponent extends BaseFormComponent<KnowledgeBaseFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public aiKnowledgeBase: KnowledgeBaseFormInterface = <any>{};

  public dto = KnowledgeBaseFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public categories = Object.values(KnowledgeBaseCategoryEnum);

  public constructor(
    private readonly aiKnowledgeBaseService: AiKnowledgeBaseService,
    private readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.aiKnowledgeBaseService
      .findById<KnowledgeBaseFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withDeleted: true
      })
      .subscribe({
        next: (aiKnowledgeBase) => {
          this.aiKnowledgeBase = aiKnowledgeBase;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveKnowledgeBase() {
    if (this.checkValidate(this.aiKnowledgeBase).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.aiKnowledgeBaseService
          .update<KnowledgeBaseFormInterface, KnowledgeBaseFindInterface>(this.aiKnowledgeBase)
          .subscribe({
            next: (aiKnowledgeBase) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`База знаний "${this.aiKnowledgeBase.title}" обновлена`);
              this.formStoreService.saveNext(aiKnowledgeBase, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.aiKnowledgeBaseService.create<KnowledgeBaseFindInterface>(this.aiKnowledgeBase).subscribe({
          next: (aiKnowledgeBase) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('База знаний создана');
            this.formStoreService.saveNext(aiKnowledgeBase, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteKnowledgeBase() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить базу знаний "${this.aiKnowledgeBase.title}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.aiKnowledgeBaseService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`База знаний "${this.aiKnowledgeBase.title}" удалена`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreKnowledgeBase() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить базу знаний "${this.aiKnowledgeBase.title}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.aiKnowledgeBaseService.restore(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.aiKnowledgeBase = { ...this.aiKnowledgeBase, deleted: false } as any;
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`База знаний "${this.aiKnowledgeBase.title}" восстановлена`);
            this.formStoreService.saveNext(this.aiKnowledgeBase, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public setFile(files: FileFindInterface[]) {
    this.aiKnowledgeBase.file = files[0];
  }
}
